import { useState, useEffect, useRef } from "react";
import styles from "./GridPlugin.module.css";
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';

/* ── DATA ── */
const FEATURES = [
  {
    icon: "🗂️",
    title: "Content Management",
    desc: "Structured 3-level hierarchy with auto-generated shortcodes.",
    items: [
      "Named grid layouts → unique shortcodes",
      "Categories & sub-categories with images",
      "Optional connector images between cards",
      "Full TinyMCE WYSIWYG editor support",
    ],
  },
  {
    icon: "⚙️",
    title: "Admin Interface",
    desc: "3-panel dashboard built for power users.",
    items: [
      "Inline image upload via Media Library",
      "Live preview before saving",
      "Edit, Delete, Duplicate per record",
      "Bulk actions + pagination (20/page)",
    ],
  },
  {
    icon: "🖥️",
    title: "Frontend Display",
    desc: "Responsive grid cards with built-in filtering.",
    items: [
      "Category filtering via URL params",
      "Built-in search functionality",
      "Configurable per_page shortcode attr",
      "Dedicated frontend CSS stylesheet",
    ],
  },
  {
    icon: "🔌",
    title: "WordPress Integration",
    desc: "Deep WP integration with zero friction.",
    items: [
      "Page metabox — no shortcode copy-paste",
      'Custom "Category Grid Layout" template',
      "Auto-redirect on plugin activation",
      "Nonce-based security on all forms",
    ],
  },
];

const TECH = [
  { icon: "🐘", name: "PHP", desc: "WordPress Plugin API, hooks, filters, options storage" },
  { icon: "⚡", name: "JavaScript", desc: "jQuery admin interactions, media uploader, live preview" },
  { icon: "🗄️", name: "Options API", desc: "Lightweight WP data storage — zero custom DB tables" },
  { icon: "🔖", name: "Shortcode API", desc: "Dynamic shortcode registration with attribute support" },
  { icon: "🖼️", name: "Media Library", desc: "Native WP media uploader integration per field" },
  { icon: "🎨", name: "CSS", desc: "Separate admin and frontend stylesheets, responsive grid" },
];

const STANDOUT = [
  {
    icon: "🪶",
    title: "Zero Database Tables",
    desc: "Uses WordPress Options API exclusively — no schema migrations, no database overhead, no cleanup on uninstall complexity.",
  },
  {
    icon: "🔒",
    title: "Security-First Architecture",
    desc: "Nonce verification, capability checks, and input sanitization on every single form action — not bolted on after.",
  },
  {
    icon: "📦",
    title: "Fully Self-Contained",
    desc: "No external libraries, no npm dependencies, no CDN calls. Ships as a single plugin folder.",
  },
  {
    icon: "👁️",
    title: "UX-Focused Admin",
    desc: "Live preview, bulk actions, inline filtering, and duplicate shortcuts. Built for content managers, not just developers.",
  },
  {
    icon: "🔗",
    title: "Flexible Embedding",
    desc: "Use the auto-generated shortcode anywhere, or assign via the page editor metabox — whichever fits the workflow.",
  },
  {
    icon: "🧩",
    title: "3-Level Hierarchy",
    desc: "Grid Layout → Categories → Sub-categories. A structured content model that maps to real-world information architectures.",
  },
];

const ARCH_CARDS = [
  { title: "Plugin Bootstrap", file: "custom-shortcode-manager.php", desc: "Core activation hook, auto-redirect, stylesheet enqueueing, and plugin initialisation." },
  { title: "Grid Layout UI", file: "admin-shortcodes.php", desc: "Creation and management of named grid layouts, shortcode generation and listing." },
  { title: "Category Manager", file: "admin-categories.php", desc: "Category CRUD with inline image upload, connector image support, and filtering." },
  { title: "Sub-category Content", file: "subcategory-content.php", desc: "Rich-text content UI with TinyMCE editor, bulk actions, and pagination." },
  { title: "Shortcode Renderer", file: "shortcode-render.php", desc: "Shortcode registration, per_page attribute handling, search and category filtering logic." },
  { title: "Frontend Template", file: "csm-template.php", desc: "Custom page template — responsive grid HTML output for the frontend display." },
];

/* ── COMPONENT ── */
export default function PluginPage() {
  const revealRefs = useRef([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add(styles.visible); }),
      { threshold: 0.1 }
    );
    revealRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  });

  const r = (el) => { if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el); };

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.page}>

      {/* HERO */}
      <section className={styles.hero}>
        <div className={styles.heroBgGrid} />
        <div className={styles.heroLeft}>
          <span className={styles.heroEyebrow}>WordPress Plugin · GPLv2</span>
          <h1 className={styles.heroTitle}>
            Custom Grid Layout<br />
            <em>Manager</em>
          </h1>
          <p className={styles.heroDesc}>
            Create and manage dynamic, responsive grid layouts with a structured
            3-level content hierarchy. Embed anywhere with auto-generated shortcodes —
            zero coding required.
          </p>
          <div className={styles.heroMeta}>
            {["WP 5.0+", "PHP 7.2+", "Tested WP 6.9", "GPLv2", "No Dependencies"].map((t) => (
              <span key={t} className={styles.metaChip}>{t}</span>
            ))}
          </div>
          {/* <div className={styles.heroActions}>
            <button className={styles.btnPrimary}><span>View Documentation</span></button>
          </div> */}
        </div>

        {/* Image */}
        <div className={styles.heroRight}>
          <img src={require('../../../images/grid-plugin-2.png')} alt="GridPlugin preview" className={styles.heroImg} />
        </div>
      </section>

      {/* STATS */}
      <div className={styles.statsStrip} ref={r}>
        {[
          { num: "3", unit: "-level", label: "Content Hierarchy" },
          { num: "0", unit: " tables", label: "DB Footprint" },
          { num: "6", unit: " files", label: "Plugin Includes" },
          { num: "100", unit: "%", label: "WP Native APIs" },
        ].map((s) => (
          <div key={s.label} className={styles.statItem}>
            <div className={styles.statNum}>{s.num}<span>{s.unit}</span></div>
            <div className={styles.statLabel}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* FEATURES */}
      <div className={styles.sectionWrap} id="features">
        <div className={`${styles.sectionHead} ${styles.reveal}`} ref={r}>
          <div>
            <p className={styles.sectionEyebrow}>Capabilities</p>
            <h2 className={styles.sectionTitle}>Everything in <span className={styles.sectionTitleItalic}>one plugin</span></h2>
          </div>
          <p className={styles.sectionDesc}>
            Four focused capability areas — content management, admin UX, frontend rendering, and deep WordPress integration.
          </p>
        </div>
        <div className={`${styles.featGrid} ${styles.reveal}`} ref={r}>
          {FEATURES.map((f) => (
            <div key={f.title} className={styles.featCard}>
              <div className={styles.featIcon}>{f.icon}</div>
              <div className={styles.featTitle}>{f.title}</div>
              <p className={styles.featDesc}>{f.desc}</p>
              <ul className={styles.featList}>
                {f.items.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>


      {/* ARCHITECTURE */}
      <div className={styles.sectionWrap}>
        <div className={`${styles.sectionHead} ${styles.reveal}`} ref={r}>
          <div>
            <p className={styles.sectionEyebrow}>Architecture</p>
            <h2 className={styles.sectionTitle}>Clean <span className={styles.sectionTitleItalic}>structure</span></h2>
          </div>
          <p className={styles.sectionDesc}>
            Six focused include files, each with a single responsibility. Easy to navigate, extend, and maintain.
          </p>
        </div>
        <div className={`${styles.archGrid} ${styles.reveal}`} ref={r}>
          {/* <div className={styles.archImg}>
            <img src={require('../../../images/portfolio-2.jpg')} alt="Plugin architecture" className={styles.heroImg} />
          </div> */}

          <div className={styles.archCards}>
            {ARCH_CARDS.map((c, i) => (
              <div key={c.title} className={styles.archCard}>
                <div className={styles.archCardNum}>{String(i + 1).padStart(2, "0")}</div>
                <div>
                  <div className={styles.archCardTitle}>{c.title}</div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.68rem", color: "var(--blue)", marginBottom: "0.35rem" }}>{c.file}</div>
                  <div className={styles.archCardDesc}>{c.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* TECH STACK */}
      <div className={`${styles.sectionWrap} ${styles.sectionWrapDark}`}>
        <div className={`${styles.sectionHead} ${styles.reveal}`} ref={r}>
          <div>
            <p className={styles.sectionEyebrow}>Tech Stack</p>
            <h2 className={`${styles.sectionTitle} ${styles.onDark}`}>Built on <span className={styles.sectionTitleItalic}>WP native</span></h2>
          </div>
          <p className={`${styles.sectionDesc} ${styles.onDark}`}>
            No external dependencies — every layer uses WordPress's own APIs, keeping the plugin lightweight and update-safe.
          </p>
        </div>
        <div className={`${styles.techRow} ${styles.reveal}`} ref={r}>
          {TECH.map((t) => (
            <div key={t.name} className={styles.techCard}>
              <span className={styles.techCardIcon}>{t.icon}</span>
              <div className={styles.techCardName}>{t.name}</div>
              <p className={styles.techCardDesc}>{t.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* WHAT MAKES IT STAND OUT */}
      <div className={`${styles.sectionWrap} ${styles.sectionWrapAlt}`}>
        <div className={`${styles.sectionHead} ${styles.reveal}`} ref={r}>
          <div>
            <p className={styles.sectionEyebrow}>Differentiators</p>
            <h2 className={styles.sectionTitle}>What makes it <span className={styles.sectionTitleItalic}>stand out</span></h2>
          </div>
          <p className={styles.sectionDesc}>
            Thoughtful decisions throughout — from architecture to UX — that separate this from generic WordPress plugins.
          </p>
        </div>
        <div className={`${styles.standoutGrid} ${styles.reveal}`} ref={r}>
          {STANDOUT.map((s) => (
            <div key={s.title} className={styles.standoutCard}>
              <span className={styles.standoutIcon}>{s.icon}</span>
              <div className={styles.standoutTitle}>{s.title}</div>
              <p className={styles.standoutDesc}>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* SCREENSHOTS */}
      <div className={`${styles.sectionWrap} ${styles.sectionWrapDark}`}>
        <div className={styles.sectionHead}>
          <div>
            <p className={styles.sectionEyebrow}>Screenshots</p>
            <h2 className={`${styles.sectionTitle} ${styles.onDark}`}>Plugin in <span className={styles.sectionTitleItalic}>action</span></h2>
          </div>
          <p className={`${styles.sectionDesc} ${styles.onDark}`}>A look at the admin dashboard and frontend output.</p>
        </div>
        <div className={styles.ssGrid}>
          {[
            { img: require('../../../images/grid-plugin-2.png'), label: 'Grid Layout Manager — Admin Dashboard' },
            { img: require('../../../images/grid-plugin-3.png'), label: 'Category Manager with Image Upload' },
            { img: require('../../../images/grid-plugin-4.png'), label: 'Sub-category Content Editor (TinyMCE)' },
            { img: require('../../../images/grid-plugin-5.png'), label: 'Frontend Grid Output — Category View' },
            { img: require('../../../images/grid-plugin.png'), label: 'Shortcode Embed in Page Editor' },
            { img: require('../../../images/grid-plugin-6.png'), label: 'Search & Filter — Live Results' },
          ].map((s, i) => (
            <div key={i} className={styles.ssCard} style={{ animationDelay: `${i * 0.08}s` }}>
              <div className={styles.ssImgWrap}>
                <img src={s.img} alt={s.label} className={styles.ssImg} />
                {/* <div className={styles.ssOverlay}>
                  <span className={styles.ssZoom}>⤢</span>
                </div> */}
              </div>
              {/* <div className={styles.ssLabel}>{s.label}</div> */}
            </div>
          ))}
        </div>
      </div>



    </div>
      <Footer />
    </>
  );
}